import { useState } from 'react';
import { Button, Checkbox, Modal, SearchBar } from '@shared/components';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useScopedSecurityGroupsForProductQuery } from '@/features/security-admin/hooks/useScopedSecurityGroupsForProductQuery';
import { useAttachSecurityGroupsMutation } from '@/features/security-admin/hooks/useAttachSecurityGroupsMutation';

const MAX_ATTACH = 200;

interface Props {
  productId: string;
  parentDealerId: string | null;
  attachedGroupIds: string[];
  onClose: () => void;
}

export function AttachSecurityGroupsModal({ productId, parentDealerId, attachedGroupIds, onClose }: Props) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [outOfScopeIds, setOutOfScopeIds] = useState<string[]>([]);

  const debouncedSearch = useDebounce(search, 300);

  const groupsQuery = useScopedSecurityGroupsForProductQuery(productId, parentDealerId, debouncedSearch);
  const allGroups = groupsQuery.data?.data ?? [];
  const candidates = allGroups.filter((g) => !attachedGroupIds.includes(g.id));

  const attachMutation = useAttachSecurityGroupsMutation(productId);

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const overCap = selected.length > MAX_ATTACH;

  const handleConfirm = () => {
    if (overCap || selected.length === 0) return;
    attachMutation.mutate(selected, {
      onSuccess: () => {
        setSelected([]);
        onClose();
      },
      onError: (err: unknown) => {
        const ids =
          (err as { response?: { data?: { out_of_scope_ids?: string[] } } })
            ?.response?.data?.out_of_scope_ids ?? [];
        setOutOfScopeIds(ids);
        // Deselect out-of-scope rows so the user can retry attaching the
        // remaining in-scope selections without closing the modal.
        if (ids.length > 0) {
          setSelected((prev) => prev.filter((id) => !ids.includes(id)));
        }
      },
    });
  };

  return (
    <Modal isOpen onClose={onClose} className="max-w-lg mx-4" ariaLabel="Attach to Security Group">
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-semibold">Attach to Security Group</h2>

        <SearchBar
          searchTerm={search}
          setSearchTerm={setSearch}
          placeholder="Search security groups"
        />

        <div className="space-y-2 max-h-64 overflow-y-auto">
          {parentDealerId === null ? (
            <p className="text-sm text-mp-muted">
              This product has no owner dealer, so there are no security groups it can be attached to.
            </p>
          ) : (
            <>
              {groupsQuery.isLoading && <p className="text-sm text-mp-muted">Loading...</p>}
              {groupsQuery.isError && (
                <p className="text-mp-error text-sm">Unable to load security groups. Please try again.</p>
              )}
              {!groupsQuery.isLoading && !groupsQuery.isError && candidates.length === 0 && (
                <p className="text-sm text-mp-muted">No security groups available.</p>
              )}
              {candidates.map((group) => {
                const isOutOfScope = outOfScopeIds.includes(group.id);
                const isChecked = selected.includes(group.id);
                return (
                  <div key={group.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`sg-${group.id}`}
                      checked={isChecked}
                      onChange={() => !isOutOfScope && toggle(group.id)}
                      disabled={isOutOfScope || attachMutation.isPending}
                      ariaLabel={group.name}
                    />
                    <span className="text-sm">
                      {group.name}
                      {isOutOfScope && (
                        <span className="text-mp-muted ml-1">(out of scope)</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <p className="text-xs text-mp-muted">
          Selected: {selected.length} / {MAX_ATTACH}
        </p>

        {overCap && (
          <p className="text-mp-error text-xs">
            You can attach a maximum of {MAX_ATTACH} security groups at a time.
          </p>
        )}

        {attachMutation.isError && outOfScopeIds.length === 0 && (
          <p className="text-mp-error text-xs">Failed to attach to security groups. Please try again.</p>
        )}

        {attachMutation.isError && outOfScopeIds.length > 0 && (
          <p className="text-mp-error text-xs">
            Some security groups could not be attached. Items marked &ldquo;out of scope&rdquo; are unavailable.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={attachMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleConfirm}
            disabled={selected.length === 0 || overCap || attachMutation.isPending}
          >
            {attachMutation.isPending ? 'Attaching...' : 'Attach'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
